/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * @file appReducer.ts
 * Exports modules for managing the state used across the application.
 */
import { prop, cond, equals, always, T } from 'ramda';
import type { AppState } from '../types';
import {
  dispatchNext,
  dispatchPrevious,
  dispatchAnswerSingle,
  getFirstQuestion,
  dispatchAnswerInput,
  dispatchCalcAppPoints,
  dispatchLast,
  dispatchScope,
  dispatchResetSubOption,
  getData,
} from '../utility';
import dispatchPrice from '../utility/dispatchPrice';

/**
 * App reducer specifies how the state is changed in response to actions.
 *
 * This is one of the most important parts of this React App. The reduce updates
 * the appState based on an appAction. These actions are called from various
 * components of the application, such as the start, next, and previous buttons.
 *
 * @param {AppState} appState
 *   The application state before applying actions.
 * @param {Object} action
 *   Dispatch action object.
 *
 * @returns {AppState}
 *   The application state object after dispatching actions.
 */
export const appReducer = (appState: AppState, action: any): AppState => {
  // Loading the data on app init from external API
  const enviziAppData = cond([
    [equals('appData'), () => getData(action)],
    [T, always(prop('appData')(appState))],
  ])(prop('type')(action));
  // Tracks the currently viewed question. Empty if on the welcome page.
  const currentQuestion = cond([
    // If a 'start' action is dispatched, then reset to the first question.
    [equals('start'), () => getFirstQuestion(appState)],
    // If a 'next' or 'previous' action is dispatched, then update the current
    // question to the next or previous question id. Since some questions are
    // dependant on submitted values, the values must be recalculated each time.
    [equals('next'), () => dispatchNext(appState, action)],
    [equals('last'), () => dispatchLast(appState)],
    [equals('previous'), () => dispatchPrevious(appState, action)],
    // Otherwise, do not change the current question for this action.
    [T, always(prop('currentQuestion')(appState))],
  ])(prop('type')(action));

  // Stores all submitted answers.
  const submitted = cond([
    // Update stored answers for a radio tile or dropdown or input (single answer) control.
    [equals('answerSingle'), () => dispatchAnswerSingle(appState, action)],
    [equals('answerInput'), () => dispatchAnswerInput(appState, action)],
    // Reset the sub question for Scope 3
    [equals('resetSubOption'), () => dispatchResetSubOption(appState)],
    [T, always(prop('submitted')(appState))],
  ])(prop('type')(action));

  // Stores the scores derived from submitted answers. The score is recalculated
  // each time a user changes an answer.
  const appPoints = cond([
    [equals('answerSingle'), () => dispatchCalcAppPoints(appState)],
    [equals('answerInput'), () => dispatchCalcAppPoints(appState)],
    [T, always(prop('appPoints')(appState))],
  ])(prop('type')(action));

  const price = cond([
    [equals('price'), () => dispatchPrice(action)],
    [T, always(prop('price')(appState))],
  ])(prop('type')(action));

  const scope3State = cond([
    [equals('scope3'), () => dispatchScope(action)],
    [T, always(prop('scope3')(appState))],
  ])(prop('type')(action));

  // The AppState object is shared across the application via context hook.
  const updatedState = {
    // The source data does not change over the lifetime of the application.
    // Return the original values for dataSource and appData.
    dataSource: prop('dataSource')(appState),
    appData: enviziAppData,
    // Update all other appState values.
    currentQuestion: currentQuestion,
    submitted: submitted,
    appPoints: appPoints,
    price: price,
    scope3: scope3State,
  };

  // Save a copy of the appState to local storage. This is useful for users who
  // navigate away from the app and return. The stored appState is loaded in
  // the getAppContextInit function.
  const store = `${prop('dataSource')(appState)}-appState`;
  window.localStorage.setItem(store, JSON.stringify(updatedState));

  // resetting the state of context and leaving appData as it is to avoid calling api to load data,
  // if the reset action is dispatched.
  if (prop('type')(action) === 'reset') {
    updatedState.appPoints = 0;
    updatedState.currentQuestion = 'Q1';
    updatedState.price = {};
    updatedState.submitted = {};
    updatedState.scope3 = '';
  }
  return updatedState;
};
